(function (global) {
  'use strict';

  const REQUIRED_EVENTS = Object.freeze([
    'view_item',
    'select_flavor',
    'select_pack_size',
    'bundle_started',
    'bundle_item_added',
    'bundle_item_removed',
    'bundle_completed',
    'purchase_option_selected',
    'subscription_frequency_selected',
    'add_to_cart',
    'view_cart',
    'begin_checkout',
    'purchase',
    'taste_panel_opened',
    'taste_panel_completed',
    'health_landing_viewed',
    'real_life_moment_viewed',
    'article_cta_clicked',
    'podcast_cta_clicked'
  ]);

  const clean = value => String(value == null ? '' : value).trim().slice(0, 160);
  const safeJSON = value => {
    try { return JSON.parse(value || '{}'); } catch (_) { return {}; }
  };
  const sanitizeDetail = detail => {
    const blocked = new Set(['email', 'customer_email', 'phone', 'phone_number', 'address', 'postal_address', 'customer_id', 'comment', 'comments']);
    return Object.fromEntries(Object.entries(detail || {}).filter(([key]) => !blocked.has(String(key).toLowerCase())));
  };
  const deviceBucket = win => win.matchMedia?.('(max-width: 599px)').matches
    ? 'mobile'
    : win.matchMedia?.('(max-width: 989px)').matches ? 'tablet' : 'desktop';
  const referrerDomain = (doc, URLClass) => {
    try { return doc.referrer ? new URLClass(doc.referrer).hostname : ''; } catch (_) { return ''; }
  };

  function createAnalytics(win, options = {}) {
    if (!win || !win.document || !win.location) throw new Error('A browser-like window is required');
    const doc = win.document;
    const theme = options.theme || win.joburgTheme || {};
    const URLSearchParamsClass = win.URLSearchParams || URLSearchParams;
    const URLClass = win.URL || URL;
    const params = new URLSearchParamsClass(win.location.search || '');
    const allowedAudiences = new Set(['performance', 'fitness', 'on_the_go', 'travel', 'heritage_kosher', 'kosher', 'whole_food', 'parents', 'low_carb']);
    const requestedAudience = clean(params.get('audience')).toLowerCase();
    const currentTouch = {
      audience: allowedAudiences.has(requestedAudience) ? requestedAudience : 'general',
      utm_source: clean(params.get('utm_source')),
      utm_medium: clean(params.get('utm_medium')),
      utm_campaign: clean(params.get('utm_campaign')),
      utm_content: clean(params.get('utm_content')),
      utm_term: clean(params.get('utm_term')),
      landing_path: clean(win.location.pathname || '/'),
      referrer_domain: clean(referrerDomain(doc, URLClass))
    };
    let firstTouch = {};
    try {
      firstTouch = safeJSON(win.sessionStorage.getItem('joburgAttributionV1'));
      if (params.has('audience') || params.has('utm_source') || !firstTouch.landing_path) {
        firstTouch = currentTouch;
        win.sessionStorage.setItem('joburgAttributionV1', JSON.stringify(firstTouch));
      }
    } catch (_) { firstTouch = currentTouch; }
    const attribution = Object.freeze({
      ...currentTouch,
      ...firstTouch,
      page_audience: currentTouch.audience,
      device_bucket: deviceBucket(win)
    });
    let sequence = 0;
    const track = (eventName, detail = {}) => {
      const event = clean(eventName);
      if (!event) throw new Error('An analytics event name is required');
      const safeDetail = sanitizeDetail(detail);
      let selectedMoment = '';
      try { selectedMoment = clean(win.sessionStorage.getItem('joburgRealLifeMoment')); } catch (_) {}
      sequence += 1;
      const valueCents = Number(safeDetail.value_cents ?? safeDetail.price_cents);
      const payload = {
        event,
        event_id: `joburg-${Date.now()}-${sequence}`,
        event_timestamp: new Date().toISOString(),
        currency: clean(safeDetail.currency || theme.currency || 'USD'),
        page_type: clean(theme.pageType || ''),
        page_path: clean(win.location.pathname || '/'),
        page_title: clean(doc.title || ''),
        taxonomy_status: REQUIRED_EVENTS.includes(event) ? 'required' : 'supporting',
        ...attribution,
        real_life_moment: selectedMoment,
        ...safeDetail
      };
      if (Number.isFinite(valueCents)) {
        payload.value_cents = Math.round(valueCents);
        payload.value = Number((payload.value_cents / 100).toFixed(2));
      }
      win.dataLayer = win.dataLayer || [];
      win.dataLayer.push(payload);
      if (typeof win.CustomEvent === 'function') doc.dispatchEvent(new win.CustomEvent(`joburg:${event}`, {detail: payload}));
      return payload;
    };
    return Object.freeze({REQUIRED_EVENTS, attribution, track});
  }

  const api = Object.freeze({REQUIRED_EVENTS, createAnalytics});
  global.JoburgAnalyticsCore = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global.document && global.location) global.joburgAnalytics = createAnalytics(global);
})(typeof window !== 'undefined' ? window : globalThis);
